import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Login.css";
import publicApi from "../../apis/publicApi";
import ButtonSpinner from "../../components/ButtonSpinner/ButtonSpinner";
import Notification from "../../components/Notification/Notification";
import { setIsLoggedIn } from "../../redux/slices/isLoggedInSlice";
import { setRole, clearRole } from "../../redux/slices/roleSlice";
import { setPermissions, clearPermissions } from "../../redux/slices/permissionsSlice";

const Login = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // login | forgot | forgot-sent
    const [step, setStep] = useState("login");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [forgotEmail, setForgotEmail] = useState("");

    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "info",
    });

    const resetNotification = () => {
        setNotification({
            show: false,
            message: "",
            type: "info",
        });
    };

    /* ──────────────────────────────────────────────
       LOGIN
    ────────────────────────────────────────────── */
    const handleSubmit = async (e) => {
        e.preventDefault();

        setLoading(true);
        resetNotification();

        try {
            const res = await publicApi.post("/api/auth/login", {
                email: email.trim().toLowerCase(),
                password,
            });

            if (res?.data?.user) {
                dispatch(setIsLoggedIn(true));
                dispatch(setRole(res.data.role));
                dispatch(setPermissions(res.data.role.permissions));

                setNotification({
                    show: true,
                    message: res.data.message || "Login successful!",
                    type: "success",
                });

                setTimeout(() => {
                    navigate("/dashboard", {
                        state: { message: res.data.message },
                    });
                }, 1000);
            } else {
                dispatch(setIsLoggedIn(false));
                dispatch(clearRole());
                dispatch(clearPermissions());

                setNotification({
                    show: true,
                    message: res?.data?.message || "Unable to login.",
                    type: "error",
                });
            }
        } catch (err) {
            console.error(err);

            dispatch(setIsLoggedIn(false));
            dispatch(clearRole());
            dispatch(clearPermissions());

            setNotification({
                show: true,
                message:
                    err?.response?.data?.message ||
                    "Login failed. Please check your credentials.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    /* ──────────────────────────────────────────────
       FORGOT PASSWORD
    ────────────────────────────────────────────── */
    const handleForgotPassword = async (e) => {
        e.preventDefault();

        if (!forgotEmail.trim()) {
            setNotification({
                show: true,
                message: "Please enter your email address.",
                type: "error",
            });
            return;
        }

        setLoading(true);
        resetNotification();

        try {
            const { data } = await publicApi.post(
                "/api/auth/forgot-password",
                {
                    email: forgotEmail.trim().toLowerCase(),
                }
            );

            if (!data?.success) {
                throw new Error(
                    data?.message || "Unable to process request."
                );
            }

            setStep("forgot-sent");
        } catch (err) {
            setNotification({
                show: true,
                message:
                    err?.response?.data?.message ||
                    err?.message ||
                    "Unable to send reset link.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-card">

                {/* HEADER */}
                <div className="login-card-header">
                    <div className="login-flag">🇳🇬</div>

                    <h1 className="login-title">
                        Government Budget Portal
                    </h1>

                    {step === "login" && (
                        <p className="login-subtitle">
                            Sign in to your account to continue
                        </p>
                    )}

                    {step === "forgot" && (
                        <p className="login-subtitle">
                            Enter your email address to receive a password
                            reset link
                        </p>
                    )}

                    {step === "forgot-sent" && (
                        <p className="login-subtitle">
                            Password reset instructions have been sent
                        </p>
                    )}
                </div>

                <Notification
                    show={notification.show}
                    message={notification.message}
                    type={notification.type}
                    onClose={() =>
                        setNotification((p) => ({
                            ...p,
                            show: false,
                        }))
                    }
                />

                {/* ───────────────────────── LOGIN ───────────────────────── */}
                {step === "login" && (
                    <form
                        className="login-form-body"
                        onSubmit={handleSubmit}
                    >
                        <div className="login-field">
                            <label
                                className="login-label"
                                htmlFor="email"
                            >
                                Email Address
                            </label>

                            <input
                                id="email"
                                className="login-input"
                                type="email"
                                placeholder="you@example.gov.ng"
                                value={email}
                                onChange={(e) =>
                                    setEmail(e.target.value)
                                }
                                required
                                autoComplete="email"
                            />
                        </div>

                        <div className="login-field">
                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: 6,
                                }}
                            >
                                <label
                                    className="login-label"
                                    htmlFor="password"
                                >
                                    Password
                                </label>

                                <button
                                    type="button"
                                    className="login-forgot"
                                    onClick={() => {
                                        resetNotification();
                                        setStep("forgot");
                                    }}
                                >
                                    Forgot password?
                                </button>
                            </div>

                            <div
                                style={{
                                    position: "relative",
                                }}
                            >
                                <input
                                    id="password"
                                    className="login-input"
                                    type={
                                        showPassword
                                            ? "text"
                                            : "password"
                                    }
                                    placeholder="Enter your password"
                                    value={password}
                                    onChange={(e) =>
                                        setPassword(
                                            e.target.value
                                        )
                                    }
                                    required
                                    autoComplete="current-password"
                                    style={{
                                        paddingRight: "50px",
                                    }}
                                />

                                <button
                                    type="button"
                                    onClick={() =>
                                        setShowPassword(
                                            (prev) => !prev
                                        )
                                    }
                                    style={{
                                        position: "absolute",
                                        right: "12px",
                                        top: "50%",
                                        transform:
                                            "translateY(-50%)",
                                        border: "none",
                                        background: "transparent",
                                        cursor: "pointer",
                                        color: "#666",
                                        fontSize: "13px",
                                        fontWeight: 600,
                                    }}
                                >
                                    {showPassword
                                        ? "Hide"
                                        : "Show"}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="login-submit-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <ButtonSpinner
                                    size={10}
                                    textColor="#fff"
                                    backgroundColor="#fff"
                                    loadingInfo="Signing in..."
                                    color="transparent"
                                />
                            ) : (
                                "Sign In →"
                            )}
                        </button>

                        <p className="login-footer">
                            Federal Republic of Nigeria · Public
                            Finance Portal
                        </p>
                    </form>
                )}

                {/* ───────────────────── FORGOT PASSWORD ─────────────────── */}
                {step === "forgot" && (
                    <form
                        className="login-form-body"
                        onSubmit={handleForgotPassword}
                    >
                        <div className="login-field">
                            <label
                                className="login-label"
                                htmlFor="forgotEmail"
                            >
                                Email Address
                            </label>

                            <input
                                id="forgotEmail"
                                className="login-input"
                                type="email"
                                placeholder="you@example.gov.ng"
                                value={forgotEmail}
                                onChange={(e) =>
                                    setForgotEmail(
                                        e.target.value
                                    )
                                }
                                required
                                autoComplete="email"
                            />
                        </div>

                        <button
                            type="submit"
                            className="login-submit-btn"
                            disabled={loading}
                        >
                            {loading ? (
                                <ButtonSpinner
                                    size={10}
                                    textColor="#fff"
                                    backgroundColor="#fff"
                                    loadingInfo="Sending..."
                                    color="transparent"
                                />
                            ) : (
                                "Send Reset Link →"
                            )}
                        </button>

                        <button
                            type="button"
                            className="login-back-btn"
                            onClick={() => {
                                resetNotification();
                                setStep("login");
                            }}
                        >
                            ← Back to Sign In
                        </button>
                    </form>
                )}

                {/* ───────────────────── SUCCESS SCREEN ─────────────────── */}
                {step === "forgot-sent" && (
                    <div
                        style={{
                            textAlign: "center",
                            padding: "10px 0",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "60px",
                                marginBottom: "15px",
                            }}
                        >
                            ✅
                        </div>

                        <h3
                            style={{
                                marginBottom: "12px",
                            }}
                        >
                            Check Your Inbox
                        </h3>

                        <p
                            style={{
                                color: "#666",
                                lineHeight: 1.6,
                                marginBottom: "25px",
                            }}
                        >
                            If an account exists for{" "}
                            <strong>{forgotEmail}</strong>,
                            a password reset link has been sent.
                            Please also check your spam folder.
                        </p>

                        <button
                            className="login-submit-btn"
                            onClick={() => {
                                setForgotEmail("");
                                resetNotification();
                                setStep("login");
                            }}
                        >
                            Back to Sign In
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Login;